import { QueryKeyEnum } from '@/api/model/QueryKey.enum.ts';
import Clock from '@/assets/clock.svg';
import { useLayoutContext } from '@/utils/providers/layoutContext/useUserContext.tsx';
import { useUserContext } from '@/utils/providers/userContext/useUserContext.tsx';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import { useUpdateQuizConnectionTime } from '@/views/process/utils/useUpdateQuizConnectionTime.util.ts';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import React, { PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

type Props = PropsWithChildren & {
  hasQuestionIndex: boolean;
  quizDueTime: string | undefined;
  quizTimeToComplete?: number;
  userQuizId?: string;
};

export const ProcessTimer: React.FC<Props> = props => {
  const { children, hasQuestionIndex, quizDueTime, quizTimeToComplete, userQuizId } = props;
  const { t } = useTranslationWithPrefix('components.processTimer');
  const { isError, isUpdating, updateQuizConnectionTime } = useUpdateQuizConnectionTime();
  const [timeFinished, setTimeFinished] = useState(false);
  const { user } = useUserContext();
  const queryClient = useQueryClient();

  const calculateTimeLeft = useCallback(() => {
    if (quizDueTime === undefined) {
      return quizTimeToComplete ? `${quizTimeToComplete}:00` : '30:00';
    }
    const minutes = dayjs(quizDueTime).diff(dayjs(), 'm');
    const seconds = dayjs(quizDueTime).diff(dayjs(), 's') - 60 * minutes;

    if (minutes <= 0 && seconds <= 0) {
      setTimeFinished(true);
      return '0:00';
    }
    return minutes + ':' + (seconds < 10 ? '0' : '') + seconds;
  }, [quizDueTime, quizTimeToComplete]);

  const [timeLeft, setTimeLeft] = useState(() => calculateTimeLeft());
  // The timer sits in the header next to the language switcher
  const { headerSlot } = useLayoutContext();

  useEffect(() => {
    if (timeFinished) {
      void queryClient.invalidateQueries({
        queryKey: [QueryKeyEnum.GET_QUIZ_QUESTION, user?.userId, userQuizId],
      });
    }
  }, [queryClient, timeFinished, user?.userId, userQuizId]);

  const handleUpdateQuizConnectionTime = useCallback(async () => {
    if (isUpdating) {
      return;
    }
    if (isError) {
      setTimeout(() => {
        window.location.reload();
      }, 5000);
      return;
    }
    if (hasQuestionIndex) {
      await updateQuizConnectionTime({ userQuizId });
    }
  }, [isUpdating, isError, updateQuizConnectionTime, userQuizId, hasQuestionIndex]);

  useEffect(() => {
    const interval = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    const interval2 = setInterval(() => {
      void handleUpdateQuizConnectionTime();
    }, 5000);

    return () => {
      clearInterval(interval);
      clearInterval(interval2);
    };
  }, [calculateTimeLeft, handleUpdateQuizConnectionTime]);

  return (
    <div className="position-relative w-full">
      {!isError &&
        headerSlot &&
        createPortal(
          <div className="flex gap-2 items-center text-body">
            <img alt="clock" src={Clock} />
            {timeLeft}
          </div>,
          headerSlot,
        )}
      {isError && (
        <div className="flex justify-center">
          <div
            className="text-bg-warning p-3 text-center font-bold"
            style={{ borderRadius: '8px' }}
          >
            {t('heartBeatError')}
          </div>
        </div>
      )}
      {children}
    </div>
  );
};
